// src/pages/JoinPage.js
// Public membership signup. Embeddable in Wix via iframe (no login required).
// Collects business + primary-contact details, lets the applicant pick a
// membership level, then hands off to Cardknox to pay the annual dues. The
// payment webhook activates the member and sends the welcome email.

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/public' });

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function JoinPage() {
  const [types, setTypes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [joinedFree, setJoinedFree] = useState(null); /* [marker: free-membership-signup v1] */
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    businessName: '', firstName: '', lastName: '', email: '', phone: '', website: '',
    addressLine1: '', addressLine2: '', city: '', state: '', zip: '',
    membershipTypeId: '', industryId: '', notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([
      api.get('/membership-types').then(r => setTypes(r.data || [])).catch(() => setTypes([])),
      api.get('/industries').then(r => setIndustries(r.data || [])).catch(() => setIndustries([])),
    ]).finally(() => setLoading(false));
  }, []);

  const selectedType = types.find(t => String(t.id) === String(form.membershipTypeId));
  const dues = selectedType ? parseFloat(selectedType.annual_dues) : null;

  // Group industries by category for <optgroup>s
  const industryGroups = industries.reduce((acc, row) => {
    const cat = row.category || 'Other';
    (acc[cat] = acc[cat] || []).push(row);
    return acc;
  }, {});

  const handleSubmit = async () => {
    setError(null);
    if (!form.businessName.trim()) return setError('Please enter your business name.');
    if (!form.firstName.trim() || !form.lastName.trim()) return setError('Please enter your first and last name.');
    const mail = form.email.trim();
    if (!mail || !mail.includes('@') || !mail.includes('.')) return setError('Please enter a valid email address.');
    if (!form.membershipTypeId) return setError('Please choose a membership level.');

    setSubmitting(true);
    try {
      const res = await api.post('/join', form);
      if (res.data && res.data.free) {
        // $0 membership — nothing to pay; it is already active.
        setJoinedFree(res.data);
        setSubmitting(false);
      } else if (res.data && res.data.paymentUrl) {
        setRedirecting(true);
        window.location.href = res.data.paymentUrl;
      } else {
        setError('Could not start your payment. Please try again or contact NJJBA.');
        setSubmitting(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again or contact NJJBA.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={pageStyle}><div style={statusStyle}>Loading…</div></div>;
  }

  if (joinedFree) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div style={cardStyle}>
            <div style={bodyStyle}>
              <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 12 }}>✓</div>
              <h1 style={titleStyle}>You're a member!</h1>
              <p style={{ color: '#475569', lineHeight: 1.6 }}>
                Your <strong>{joinedFree.membershipType}</strong> membership is active — no payment
                was required. A welcome email is on its way.
              </p>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 12 }}>
                Reference: {joinedFree.reference}
              </p>
              <p style={{ color: '#475569', marginTop: 16 }}>
                Questions? Contact <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div style={cardStyle}>
            <div style={bodyStyle}>
              <h1 style={titleStyle}>Redirecting to secure payment…</h1>
              <p style={{ color: '#475569' }}>
                Please don't close this window. If you aren't redirected automatically,
                check your email for a payment link or contact{' '}
                <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={bodyStyle}>
            <h1 style={titleStyle}>Join the NJJBA</h1>
            <p style={{ color: '#475569', marginTop: -6, marginBottom: 20, lineHeight: 1.6 }}>
              Become a member of the New Jersey Jewish Business Alliance. Complete the form
              below and pay your annual dues securely to activate your membership.
            </p>

            <Field label="Business Name" required>
              <input style={inputStyle} value={form.businessName} onChange={e => set('businessName', e.target.value)} />
            </Field>

            <div style={rowStyle}>
              <Field label="First Name" required>
                <input style={inputStyle} value={form.firstName} onChange={e => set('firstName', e.target.value)} />
              </Field>
              <Field label="Last Name" required>
                <input style={inputStyle} value={form.lastName} onChange={e => set('lastName', e.target.value)} />
              </Field>
            </div>

            <div style={rowStyle}>
              <Field label="Email" required>
                <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              </Field>
              <Field label="Phone">
                <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} />
              </Field>
            </div>

            <Field label="Website">
              <input style={inputStyle} placeholder="https://" value={form.website} onChange={e => set('website', e.target.value)} />
            </Field>

            <Field label="Street Address">
              <input style={inputStyle} value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} />
            </Field>
            <Field label="Address Line 2">
              <input style={inputStyle} value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} />
            </Field>

            <div style={rowStyle}>
              <Field label="City">
                <input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} />
              </Field>
              <Field label="State">
                <select style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="ZIP">
                <input style={inputStyle} value={form.zip} onChange={e => set('zip', e.target.value)} />
              </Field>
            </div>

            {industries.length > 0 && (
              <Field label="Industry">
                <select style={inputStyle} value={form.industryId} onChange={e => set('industryId', e.target.value)}>
                  <option value="">— Select —</option>
                  {Object.keys(industryGroups).map(cat => (
                    <optgroup key={cat} label={cat}>
                      {industryGroups[cat].map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </optgroup>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Membership Level" required>
              <select style={inputStyle} value={form.membershipTypeId} onChange={e => set('membershipTypeId', e.target.value)}>
                <option value="">— Select a level —</option>
                {types.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.annual_dues != null ? ` — $${parseFloat(t.annual_dues).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/year` : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Anything you'd like us to know? (optional)">
              <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </Field>

            {dues != null && !isNaN(dues) && (
              <div style={duesBoxStyle}>
                <span>Annual dues</span>
                <strong style={{ fontSize: 20 }}>
                  ${dues.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </strong>
              </div>
            )}

            {error && <div style={errorBoxStyle}>{error}</div>}

            <button style={{ ...submitStyle, opacity: submitting ? 0.7 : 1 }} onClick={handleSubmit} disabled={submitting}>
              {submitting
                ? (dues === 0 ? 'Completing signup…' : 'Starting secure payment…')
                : (dues === 0 ? 'Complete Signup →' : 'Continue to Payment →')}
            </button>
            <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>
              {dues === 0
                ? 'No payment is required for this membership level — it activates immediately.'
                : 'Payments are processed securely. Your membership activates once payment is confirmed.'}
            </p>
          </div>
        </div>
        <footer style={footerStyle}>
          New Jersey Jewish Business Alliance · <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ flex: 1, marginBottom: 14 }}>
      <label style={labelStyle}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</label>
      {children}
    </div>
  );
}

const pageStyle = { background: '#f1f5f9', minHeight: '100vh', padding: '24px 16px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
const containerStyle = { maxWidth: 640, margin: '0 auto' };
const cardStyle = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(15,23,42,0.08)', overflow: 'hidden' };
const bodyStyle = { padding: 28 };
const titleStyle = { fontSize: 26, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' };
const statusStyle = { textAlign: 'center', padding: 48, color: '#475569', fontSize: 16 };
const rowStyle = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 5 };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 15, boxSizing: 'border-box', background: '#fff' };
const duesBoxStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', margin: '8px 0 4px', color: '#0f172a', fontWeight: 600 };
const errorBoxStyle = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: 14, margin: '12px 0' };
const submitStyle = { display: 'block', width: '100%', marginTop: 16, padding: '14px 16px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3 };
const footerStyle = { textAlign: 'center', color: '#64748b', fontSize: 13, padding: '18px 0' };
const linkStyle = { color: '#1e3a5f', fontWeight: 600 };
